const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta';

import type { BloodPressureRecord, BloodSugarRecord, AIAnalysisResult } from '@/types';

async function callGemini(prompt: string): Promise<string> {
  if (!GEMINI_API_KEY) {
    throw new Error('Gemini API Key not configured');
  }

  const response = await fetch(
    `${GEMINI_API_URL}/models/gemini-1.5-flash:generateContent?key=${GEMINI_API_KEY}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{ text: prompt }]
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 2048
        }
      })
    }
  );

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  const data = await response.json();
  return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
}

export async function analyzeHealthData(
  bpRecords: BloodPressureRecord[],
  bsRecords: BloodSugarRecord[]
): Promise<AIAnalysisResult> {
  // Prepare data summary
  const bpSummary = bpRecords.slice(0, 30).map(r => 
    `${r.date}: ${r.systolic}/${r.diastolic} mmHg${r.heartRate ? ` (心率${r.heartRate})` : ''}`
  ).join('\n');

  const bsSummary = bsRecords.slice(0, 30).map(r => 
    `${r.date}: ${r.value} mmol/L (${r.type === 'fasting' ? '空腹' : r.type === 'post_meal_1h' ? '餐后1h' : r.type === 'post_meal_2h' ? '餐后2h' : '随机'})`
  ).join('\n');

  const prompt = `作为一名健康顾问，请分析以下用户的健康数据，提供专业的分析和建议。

## 血压记录（最近30条）:
${bpSummary || '无数据'}

## 血糖记录（最近30条）:
${bsSummary || '无数据'}

请提供以下内容的分析（使用中文）：
1. 整体摘要（2-3句话）
2. 趋势变化（3-5点）
3. 异常提醒（如有，列出异常值及建议）
4. 健康建议（3-5条具体建议）

请以JSON格式返回：
{
  "summary": "整体摘要",
  "trends": ["趋势1", "趋势2", ...],
  "anomalies": ["异常1", "异常2", ...],
  "suggestions": ["建议1", "建议2", ...]
}`;

  try {
    const response = await callGemini(prompt);
    
    // Try to parse JSON from response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      return {
        summary: parsed.summary || '分析完成',
        trends: parsed.trends || [],
        anomalies: parsed.anomalies || [],
        suggestions: parsed.suggestions || []
      };
    }
    
    // Fallback: return raw text as summary
    return {
      summary: response.slice(0, 500),
      trends: [],
      anomalies: [],
      suggestions: []
    };
  } catch (error) {
    console.error('AI analysis error:', error);
    return {
      summary: '分析服务暂时不可用，请稍后重试。',
      trends: [],
      anomalies: [],
      suggestions: ['请确保已配置Gemini API Key']
    };
  }
}

export async function getHealthAdvice(
  question: string,
  bpRecords: BloodPressureRecord[],
  bsRecords: BloodSugarRecord[]
): Promise<string> {
  const bpSummary = bpRecords.slice(0, 10).map(r => 
    `${r.date}: ${r.systolic}/${r.diastolic}`
  ).join(', ');

  const bsSummary = bsRecords.slice(0, 10).map(r => 
    `${r.date}: ${r.value}`
  ).join(', ');

  const prompt = `用户健康数据：
血压记录: ${bpSummary || '无'}
血糖记录: ${bsSummary || '无'}

用户问题: ${question}

请作为健康顾问回答用户的问题。请注意：
1. 提供有帮助的建议
2. 强调这只是参考建议，不能替代医生诊断
3. 如果有明显异常，建议就医
4. 使用中文回答`;

  try {
    return await callGemini(prompt);
  } catch (error) {
    return '抱歉，AI服务暂时不可用。请检查API配置或稍后重试。';
  }
}
